import { useCallback, useMemo } from "react";

import { UniformSlot } from "@uniformdev/canvas-react";
import {
    FilterableGalleryLoaderProvider,
    type FilterableGalleryLoaderRegistration
} from "src/uniform/contexts/filterable-gallery-loader-context";

import { useLocale } from "@/libraries/web-framework/src/localization/use-locale.ts";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants.ts";

import { useWAEContext } from "../../../utils/wae-context-provider";
import { useBackfill } from "../../filterable-gallery/src/hooks/use-backfill";

import { getContentfulGalleryItems } from "./client-gql/fetch.ts";

import type {
    ContentfulGalleryDataType,
    ContentfulGalleryLocale
} from "./client-gql/types.ts";
import type { LoadAdditionalItems } from "../../filterable-gallery/src/types";

export type ContentType = ContentfulGalleryDataType;

export type ContentfulGalleryContainerProps = {
    contentType?: ContentType;
};

const DEFAULT_CONTENTFUL_LOCALE: ContentfulGalleryLocale = "en-US";
const SUPPORTED_CONTENTFUL_LOCALES = new Set<string>(
    Object.values(WAC_TO_CPAPI_LOCALE_MAP)
);

const toContentfulLocale = (
    pageLocale: string | undefined
): ContentfulGalleryLocale => {
    if (!pageLocale) return DEFAULT_CONTENTFUL_LOCALE;
    const mapped = (
        WAC_TO_CPAPI_LOCALE_MAP as Record<string, ContentfulGalleryLocale>
    )[pageLocale];
    if (mapped) return mapped;
    if (SUPPORTED_CONTENTFUL_LOCALES.has(pageLocale)) {
        return pageLocale as ContentfulGalleryLocale;
    }
    return DEFAULT_CONTENTFUL_LOCALE;
};

const ContentfulGalleryContainer = ({
    contentType = "productTemplate"
}: ContentfulGalleryContainerProps): JSX.Element => {
    const intlLocale = useLocale();
    const { locale: contextLocale } = useWAEContext();
    const locale = toContentfulLocale(contextLocale ?? intlLocale);

    const loadContentfulItems = useCallback<LoadAdditionalItems>(
        ({ loaded }) =>
            getContentfulGalleryItems({ contentType, loaded, locale }),
        [contentType, locale]
    );

    const currentRegistration = useMemo<FilterableGalleryLoaderRegistration>(
        () => ({ loader: loadContentfulItems }),
        [loadContentfulItems]
    );

    const { extraItems } = useBackfill({
        seedCount: 0,
        loader: loadContentfulItems
    });

    return (
        <FilterableGalleryLoaderProvider
            currentRegistration={currentRegistration}
            extraItems={extraItems}
        >
            <UniformSlot name="content" />
        </FilterableGalleryLoaderProvider>
    );
};

export default ContentfulGalleryContainer;
